import { Link } from 'react-router-dom';
import { ArrowLeft, AlertTriangle } from 'lucide-react';

export function Disclaimer() {
  return (
    <div className="min-h-screen bg-[#0a0f0a] text-gray-300">
      <div className="max-w-4xl mx-auto px-6 py-16">
        <Link to="/" className="inline-flex items-center gap-2 text-emerald-400 hover:text-emerald-300 mb-8">
          <ArrowLeft className="w-4 h-4" />
          Back to Home
        </Link>

        <h1 className="text-4xl font-bold text-white mb-8">Investment Disclaimer</h1>
        <p className="text-gray-400 mb-8">Last updated: January 25, 2026</p>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <AlertTriangle className="w-8 h-8 text-yellow-500 flex-shrink-0 mt-1" />
            <div>
              <h2 className="text-xl font-semibold text-yellow-400 mb-2">Important Notice</h2>
              <p className="text-yellow-200/80">
                SentimentEdge is not a registered investment advisor, broker-dealer, or financial planner.
                The information and tools provided are for educational and informational purposes only.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-8 text-gray-300 leading-relaxed">
          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Not Financial Advice</h2>
            <p>
              The content provided through SentimentEdge, including but not limited to sentiment scores,
              disconnect analysis, historical data, backtesting results, and any other analytics or
              recommendations, does not constitute investment advice, financial advice, trading advice,
              or any other sort of advice.
            </p>
            <p className="mt-4">
              You should not treat any of the platform's content as such. SentimentEdge does not recommend
              that any security, portfolio of securities, transaction, or investment strategy is suitable
              for any specific person.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Investment Risks</h2>
            <p>
              <strong className="text-white">All investments involve risk, including the loss of principal.</strong>
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>Past performance is not indicative of future results</li>
              <li>Backtested results have inherent limitations and may not reflect actual trading</li>
              <li>Sentiment analysis is speculative and should not be relied upon for investment decisions</li>
              <li>Market conditions can change rapidly and unpredictably</li>
              <li>Social media sentiment can be manipulated or misleading</li>
              <li>Historical patterns may not repeat in the future</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Backtesting Limitations</h2>
            <p>
              Backtested performance results have certain inherent limitations:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>Backtested results are hypothetical and do not represent actual trading</li>
              <li>No representation is made that any account will achieve similar results</li>
              <li>Backtests do not account for all market factors, including liquidity constraints</li>
              <li>Results may differ significantly from actual future performance</li>
              <li>Past market conditions may not repeat</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Data Accuracy</h2>
            <p>
              While we strive to provide accurate information, we make no guarantees regarding:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>The accuracy, completeness, or timeliness of sentiment data</li>
              <li>The accuracy of stock prices, fundamentals, or other market data</li>
              <li>The reliability of third-party data sources</li>
              <li>The absence of errors in calculations or analysis</li>
            </ul>
            <p className="mt-4">
              Social media data is inherently noisy and may contain spam, bots, or coordinated
              manipulation campaigns. Our AI analysis may not detect all such anomalies.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Do Your Own Research</h2>
            <p>
              Before making any investment decision, you should:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>Conduct your own thorough research and analysis</li>
              <li>Consult with a qualified financial advisor</li>
              <li>Consider your personal financial situation and risk tolerance</li>
              <li>Understand the specific risks associated with each investment</li>
              <li>Never invest more than you can afford to lose</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">No Guarantee of Profits</h2>
            <p>
              SentimentEdge makes no representation or guarantee that using our platform will result
              in profits or prevent losses. Trading securities can result in significant financial
              losses, including the loss of your entire investment.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Information</h2>
            <p>
              Our platform may display information from third-party sources including but not limited to:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>Social media platforms (X/Twitter, Reddit)</li>
              <li>Financial data providers</li>
              <li>News sources</li>
            </ul>
            <p className="mt-4">
              We are not responsible for the accuracy or reliability of third-party information.
              Views expressed in social media posts are those of the original authors, not SentimentEdge.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Limitation of Liability</h2>
            <p>
              Under no circumstances shall SentimentEdge be liable for any direct, indirect, incidental,
              special, consequential, or punitive damages arising from:
            </p>
            <ul className="list-disc list-inside mt-4 space-y-2">
              <li>Your use of or inability to use the platform</li>
              <li>Any investment decisions based on platform content</li>
              <li>Errors or omissions in platform data or analysis</li>
              <li>Unauthorized access to your account or data</li>
              <li>Any other matter relating to the platform</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Regulatory Notice</h2>
            <p>
              SentimentEdge is not registered with the Securities and Exchange Commission (SEC),
              the Financial Industry Regulatory Authority (FINRA), or any state securities regulatory
              authority. The platform is not intended to provide personalized investment recommendations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-white mb-4">Contact</h2>
            <p>
              If you have questions about this disclaimer, please contact us at{' '}
              <a href="mailto:legal@stocksentiment.io" className="text-emerald-400 hover:text-emerald-300">
                legal@stocksentiment.io
              </a>
            </p>
          </section>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row gap-4">
          <Link to="/" className="text-emerald-400 hover:text-emerald-300">
            ← Back to Home
          </Link>
          <span className="text-gray-600 hidden sm:inline">|</span>
          <Link to="/terms" className="text-emerald-400 hover:text-emerald-300">
            Terms of Service
          </Link>
          <span className="text-gray-600 hidden sm:inline">|</span>
          <Link to="/privacy" className="text-emerald-400 hover:text-emerald-300">
            Privacy Policy
          </Link>
        </div>
      </div>
    </div>
  );
}
